import { useEffect, useRef, useState } from 'react';
import { content } from '../../content';
import {
  getEnemyIntent,
  getEnemyIntentDamage,
  getOverheatDamage,
  isCardPlayable,
  resolveCardDefinition,
} from '../../core/engine';
import type {
  CardDefinition,
  EnemyInstance,
  EnemyMove,
  GameState,
  StatusMap,
  StatusId,
} from '../../core/types';
import type { CommandHandler } from '../App';
import { CardView } from '../components/CardView';
import { GameHeader } from '../components/GameHeader';
import { HeatGauge, Meter } from '../components/Meters';
import { HelpTip } from '../components/HelpTip';
import { CombatCues, type CombatFeedback } from '../components/CombatFeedback';
import { STATUS_HELP } from '../fieldGuide';

interface CombatLogEntry {
  id: number;
  text: string;
}

function intentText(state: GameState, enemy: EnemyInstance, move: EnemyMove): string {
  const projectedDamage = getEnemyIntentDamage(state, enemy, move);
  const pressure = move.kind === 'attack' || move.kind === 'attack_status'
    ? [
        move.playerHeat ? `Stoke ${move.playerHeat} Heat` : '',
        move.gearWearOnHpDamage ? `wear ${move.gearWearOnHpDamage} Gear if unblocked` : '',
        move.statusCard
          ? `add ${move.statusCard.amount} ${content.cards[move.statusCard.definitionId]?.name ?? 'Status'}`
          : '',
      ].filter(Boolean).join(' · ')
    : '';
  const withPressure = (text: string) => pressure ? `${text} · ${pressure}` : text;
  switch (move.kind) {
    case 'attack':
      return withPressure(move.hits && move.hits > 1
        ? `Attack ${projectedDamage ?? move.amount} × ${move.hits}`
        : `Attack ${projectedDamage ?? move.amount}`);
    case 'block':
      return `Raise ${move.amount} Block`;
    case 'attack_status':
      return withPressure(`Attack ${projectedDamage ?? move.amount} · Apply ${move.statusAmount} ${move.status}`);
    case 'buff':
      return `Gain ${move.amount} ${move.status}`;
  }
}

function intentKind(move: EnemyMove): string {
  if (move.kind === 'attack_status') return 'debuff';
  if (move.kind === 'buff') return 'buff';
  return move.kind;
}

function activeStatuses(statuses: StatusMap): Array<[StatusId, number]> {
  return (Object.entries(statuses) as Array<[StatusId, number]>).filter((entry) => entry[1] > 0);
}

function StatusBadge({ status, amount }: { status: StatusId; amount: number }) {
  const help = STATUS_HELP[status];
  return <HelpTip label={`${help.name} ${amount}`}>{help.description}</HelpTip>;
}

function unmetHeatRequirement(definition: CardDefinition, heat: number): number | undefined {
  if (definition.effects.length === 0) return undefined;
  let minimum = Number.POSITIVE_INFINITY;
  for (const effect of definition.effects) {
    if (effect.kind !== 'if_heat' || heat >= effect.minimum) return undefined;
    minimum = Math.min(minimum, effect.minimum);
  }
  return Number.isFinite(minimum) ? minimum : undefined;
}

interface EnemyViewProps {
  state: GameState;
  enemy: EnemyInstance;
  selected: boolean;
  targetable: boolean;
  feedback: CombatFeedback | null;
  buttonRef?: React.Ref<HTMLButtonElement>;
  onSelect: () => void;
}

function EnemyView({ state, enemy, selected, targetable, feedback, buttonRef, onSelect }: EnemyViewProps) {
  const definition = content.enemies[enemy.definitionId];
  if (!definition) return null;
  const intent = getEnemyIntent(state, enemy, content);
  const intentDescription = intentText(state, enemy, intent);
  const defeated = enemy.hp <= 0;
  const statuses = activeStatuses(enemy.statuses);

  return (
    <article className={`enemy-card${selected ? ' is-selected' : ''}${targetable ? ' is-targetable' : ''}${defeated ? ' is-defeated' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        disabled={defeated}
        aria-pressed={selected}
        aria-label={`${definition.name}, ${enemy.hp} of ${enemy.maxHp} health. Intent: ${intentDescription}${targetable ? '. Select as target' : ''}`}
        onClick={onSelect}
      >
        <span className={`intent intent--${intentKind(intent)}`}>
          <span className="intent__icon" aria-hidden="true" />
          <span><small>Projected intent</small><strong>{intentDescription}</strong></span>
        </span>
        <span className="enemy-card__portrait" data-enemy-art={definition.id} aria-hidden="true">
          <img src={`/art/cathedris/enemies/${definition.id}.webp`} alt="" decoding="async" />
        </span>
        <span className="enemy-card__name">{definition.name}</span>
        {definition.phases?.length ? (
          <span className="enemy-card__phase">Phase {enemy.phaseIndex + 1}/{definition.phases.length + 1}</span>
        ) : null}
        <span className="enemy-card__description">{definition.description}</span>
        <span className="enemy-card__health">
          <span><strong>{enemy.hp}</strong> / {enemy.maxHp} HP</span>
          <progress max={enemy.maxHp} value={enemy.hp} aria-label={`${definition.name} health`} />
        </span>
        {enemy.block > 0 ? <span className="enemy-card__block">{enemy.block} Block</span> : null}
        {targetable ? <span className="target-callout">Choose target</span> : null}
      </button>
      {statuses.length > 0 ? (
        <div className="status-list" aria-label={`${definition.name} status effects`}>
          {statuses.map(([status, amount]) => <StatusBadge key={status} status={status} amount={amount} />)}
        </div>
      ) : null}
      <CombatCues feedback={feedback} targetId={enemy.id} />
    </article>
  );
}

export function CombatScreen({
  state,
  log,
  feedback,
  onCommand,
}: {
  state: GameState;
  log: CombatLogEntry[];
  feedback: CombatFeedback | null;
  onCommand: CommandHandler;
}) {
  const combat = state.combat;
  const run = state.run;
  const [pendingCardId, setPendingCardId] = useState<string | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const firstLivingEnemy = useRef<HTMLButtonElement>(null);
  const endTurnButton = useRef<HTMLButtonElement>(null);
  const cardButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterCardPlay = useRef(false);

  useEffect(() => {
    if (pendingCardId) firstLivingEnemy.current?.focus();
  }, [pendingCardId]);

  useEffect(() => {
    const cancelTargeting = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pendingCardId) {
        cardButtons.current.get(pendingCardId)?.focus();
        setPendingCardId(null);
        setSelectedTargetId(null);
      }
    };
    window.addEventListener('keydown', cancelTargeting);
    return () => window.removeEventListener('keydown', cancelTargeting);
  }, [pendingCardId]);

  useEffect(() => {
    if (!focusAfterCardPlay.current) return;
    focusAfterCardPlay.current = false;
    const nextCard = [...cardButtons.current.values()].find(
      (button) => button.isConnected && !button.disabled,
    );
    (nextCard ?? endTurnButton.current)?.focus();
  }, [state]);

  if (!combat || !run) return null;
  const encounter = content.encounters[combat.encounterId];
  const livingEnemies = combat.enemies.filter((enemy) => enemy.hp > 0);
  const playerStatuses = activeStatuses(combat.statuses);
  const overheatDamage = getOverheatDamage(state);

  const playCard = (cardId: string, definition: CardDefinition) => {
    if (definition.target === 'enemy') {
      if (selectedTargetId && livingEnemies.some((enemy) => enemy.id === selectedTargetId)) {
        if (onCommand({ type: 'play_card', cardId, targetId: selectedTargetId })) {
          focusAfterCardPlay.current = true;
          setPendingCardId(null);
          setSelectedTargetId(null);
        }
      } else {
        setPendingCardId((current) => current === cardId ? null : cardId);
      }
      return;
    }

    if (onCommand({ type: 'play_card', cardId })) {
      focusAfterCardPlay.current = true;
      setPendingCardId(null);
      setSelectedTargetId(null);
    }
  };

  const selectEnemy = (enemyId: string) => {
    if (pendingCardId) {
      if (onCommand({ type: 'play_card', cardId: pendingCardId, targetId: enemyId })) {
        focusAfterCardPlay.current = true;
        setPendingCardId(null);
        setSelectedTargetId(null);
      }
      return;
    }
    setSelectedTargetId((current) => current === enemyId ? null : enemyId);
  };

  const endTurn = () => {
    if (onCommand({ type: 'end_turn' })) {
      setPendingCardId(null);
      setSelectedTargetId(null);
    }
  };

  let firstRefAssigned = false;

  return (
    <div className="screen combat-screen">
      <GameHeader state={state} eyebrow={encounter?.name ?? 'Unknown outpost'} />
      <main id="main-content" className="combat-layout">
        <section className="combat-hud" aria-label="Mechanic combat status">
          <Meter label="Health" value={run.hp} max={run.maxHp} tone="health" help="Health persists between fights. At zero, the expedition ends. Forges and certain events can heal you." />
          <Meter label="Energy" value={combat.energy} max={combat.maxEnergy} tone="energy" help="Pay the number on a card to play it. Energy refreshes each turn; unspent Energy is lost." />
          <Meter label="Block" value={combat.block} max={Math.max(20, combat.block)} tone="block" display={String(combat.block)} help="Absorbs incoming damage. Unused Block expires before your next turn. Overheat bypasses Block." />
          <HeatGauge heat={combat.heat} overheatDamage={overheatDamage} />
          <div className="turn-indicator">
            <span>Turn</span>
            <strong>{combat.turn}</strong>
            <small>{combat.phase === 'player' ? 'Your move' : 'Enemy move'}</small>
          </div>
          {playerStatuses.length > 0 ? (
            <div className="player-statuses" aria-label="Mechanic status effects">
              {playerStatuses.map(([status, amount]) => <StatusBadge key={status} status={status} amount={amount} />)}
            </div>
          ) : null}
          <CombatCues feedback={feedback} targetId="player" />
        </section>

        <section className="battlefield" aria-labelledby="enemy-heading">
          <div className="battlefield__heading">
            <span>
              <small>{encounter?.kind ?? 'encounter'} encounter</small>
              <h1 id="enemy-heading">{encounter?.name ?? 'The line stirs'}</h1>
            </span>
            <div className="battlefield__instructions">
              <p>{pendingCardId ? 'Choose a highlighted enemy. Press Escape to cancel.' : 'Enemy actions resolve from left to right.'}</p>
              <HelpTip label="Intents">Each enemy shows its next action, including Strength, Weak, and your Vulnerable. Burning or a boss phase change can alter what happens before an enemy acts.</HelpTip>
            </div>
          </div>
          <div className="combat-stage">
          <figure className="expedition-mechanic" aria-label="Expedition mechanic">
            <img src="/art/cathedris/characters/protagonist.webp" alt="Expedition mechanic with sealed refining harness" />
            <figcaption>Expedition Mechanic</figcaption>
          </figure>
          <div className="enemy-grid">
            {combat.enemies.map((enemy) => {
              const isFirstLiving = enemy.hp > 0 && !firstRefAssigned;
              if (isFirstLiving) firstRefAssigned = true;
              return (
                <EnemyView
                  key={enemy.id}
                  state={state}
                  enemy={enemy}
                  selected={selectedTargetId === enemy.id}
                  targetable={Boolean(pendingCardId) && enemy.hp > 0}
                  feedback={feedback}
                  buttonRef={isFirstLiving ? firstLivingEnemy : undefined}
                  onSelect={() => selectEnemy(enemy.id)}
                />
              );
            })}
          </div>
          </div>
        </section>

        <aside className="combat-sidebar" aria-labelledby="combat-log-title">
          <section className="last-action" aria-label="Last combat action" role="status" aria-atomic="true">
            <h2>{feedback?.title ?? 'Your move'}</h2>
            <p>{feedback?.summary ?? 'Read the intents, spend Energy on cards, then end your turn.'}</p>
          </section>
          <section className="pile-status" aria-label="Card piles">
            <h2>Deck cycle</h2>
            <dl>
              <div><dt>Draw</dt><dd>{combat.drawPile.length}</dd></div>
              <div><dt>Discard</dt><dd>{combat.discardPile.length}</dd></div>
              <div><dt>Exhaust</dt><dd>{combat.exhaustPile.length}</dd></div>
            </dl>
          </section>
          <section className="combat-log" role="region" aria-labelledby="combat-log-title" tabIndex={0}>
            <h2 id="combat-log-title">Combat record</h2>
            {log.length > 0 ? (
              <ol>
                {log.map((entry) => <li key={entry.id}>{entry.text}</li>)}
              </ol>
            ) : <p>The first mark has yet to be made.</p>}
          </section>
        </aside>

        <section className="hand-zone" aria-labelledby="hand-title">
          <div className="hand-zone__heading">
            <span>
              <small>Current hand · {combat.hand.length} cards</small>
              <h2 id="hand-title">Choose your tool</h2>
            </span>
            <span className="target-instruction" aria-live="polite">
              {pendingCardId ? 'Targeting: choose an enemy' : selectedTargetId ? 'Target marked — choose an Attack' : 'Select a card to play'}
            </span>
            <button ref={endTurnButton} type="button" className="end-turn-action" onClick={endTurn}>
              <span>End turn</span>
              <small>{combat.heat === 6 ? `Overheat: ${overheatDamage} HP · Enemies act` : 'Enemies act next'}</small>
            </button>
          </div>
          <div className="hand" role="group" aria-label="Cards in hand">
            {combat.hand.map((cardId) => {
              const instance = run.deck.find((card) => card.id === cardId);
              if (!instance) return null;
              const definition = resolveCardDefinition(instance, content);
              const heatRequirement = unmetHeatRequirement(definition, combat.heat);
              return (
                <CardView
                  key={cardId}
                  definition={definition}
                  instance={instance}
                  disabled={!isCardPlayable(state, cardId, content)}
                  disabledMessage={heatRequirement ? `Requires ${heatRequirement} Heat` : undefined}
                  selected={pendingCardId === cardId}
                  buttonRef={(button) => {
                    if (button) cardButtons.current.set(cardId, button);
                    else cardButtons.current.delete(cardId);
                  }}
                  onClick={() => playCard(cardId, definition)}
                />
              );
            })}
          </div>
          <div className="hand-guide" aria-label="Card help">
            <HelpTip label="Playing cards">Select a card, then its enemy target. Skills and powers play immediately. Unplayed cards enter the discard pile when you end your turn; draw 5 new cards. The discard pile shuffles when the draw pile runs out.</HelpTip>
            <HelpTip label="Gear & Durability">Playing Gear wears it by 1 Durability unless a relic protects it. At 0 it is Broken and cannot be played. Durability carries between fights; repair cards, traders, forges, and boss victories restore it.</HelpTip>
            <HelpTip label="Exhaust & Ethereal">Exhaust removes a played card for this combat. Ethereal exhausts the card if it is still in your hand at turn end. Salvage exhausts a Broken Gear card for its stated bonus. Your permanent cards return next combat.</HelpTip>
          </div>
        </section>
      </main>
    </div>
  );
}
